
"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy, Check, Play, MessageCircle, Workflow, Send } from "lucide-react";
import { IconAi, IconAutomation } from "@tabler/icons-react";

const codeExamples = {
  simple: {
    title: "Natural Language → Function Calls",
    description: "Watch how natural messages automatically trigger the right functions",
    code: `import onefunction

# Natural message automatically calls the right functions
result = onefunction({
  "task": "get customer data from slack and send follow-up email",
  "data": {"customer_id": "123"}
})

# OneFunction automatically:
# 1. Analyzes intent: "get customer data" + "send email"
# 2. Routes to: slack_api.get_user() → email_service.send()
# 3. Handles: authentication, data flow, error recovery
# 4. Returns: structured results with execution trace`,
    demo: {
      input: "get customer data from slack and send follow-up email",
      steps: [
        { icon: MessageCircle, text: "Analyzing natural language intent...", delay: 500 },
        { icon: IconAi, text: "Routing to Slack API function", delay: 1000 },
        { icon: Workflow, text: "Retrieving customer data", delay: 1500 },
        { icon: IconAutomation, text: "Calling email service function", delay: 2000 },
        { icon: Check, text: "Email sent successfully!", delay: 2500 }
      ]
    }
  },
  wrapper: {
    title: "LLM Wrapper with Smart Routing",
    description: "Drop-in replacement that optimizes function calls before LLM",
    code: `import openai
import os

# Use OneFunction as intelligent LLM wrapper
client = openai.OpenAI(
  api_key=os.environ.get("ONEFUNCTION_API_KEY"),
  base_url="https://api.onefunction.xyz/v1"
)

# Same OpenAI interface, enhanced with function routing
response = client.chat.completions.create(
  model="gpt-4",
  messages=[{
    "role": "user", 
    "content": "Analyze sales data and create report"
  }]
)

# OneFunction pre-processes and routes to optimal functions
# before sending optimized context to LLM`,
    demo: {
      input: "Analyze sales data and create report",
      steps: [
        { icon: MessageCircle, text: "Intercepting LLM request...", delay: 500 },
        { icon: IconAi, text: "Pre-processing with function routing", delay: 1000 },
        { icon: Workflow, text: "Calling analytics functions", delay: 1500 },
        { icon: IconAutomation, text: "Generating optimized context", delay: 2000 },
        { icon: Send, text: "Sending to LLM with results", delay: 2500 }
      ]
    }
  },
  local: {
    title: "Local Function Integration",
    description: "Your custom functions become part of the intelligent routing",
    code: `from onefunction import register_tool

@register_tool
def custom_database_query(query: str, table: str):
    """Custom database query function"""
    # Your local database logic
    return execute_query(query, table)

@register_tool  
def generate_dashboard_chart(data: dict, chart_type: str):
    """Generate dashboard visualization"""
    return create_chart(data, chart_type)

# OneFunction learns your local functions
result = onefunction({
  "task": "query user preferences and update dashboard",
  "data": {"user_id": "456"}
})

# Automatically routes to YOUR functions when appropriate`,
    demo: {
      input: "query user preferences and update dashboard",
      steps: [
        { icon: MessageCircle, text: "Parsing task requirements...", delay: 500 },
        { icon: IconAi, text: "Detecting local function match", delay: 1000 },
        { icon: Workflow, text: "Calling custom_database_query()", delay: 1500 },
        { icon: IconAutomation, text: "Calling generate_dashboard_chart()", delay: 2000 },
        { icon: Check, text: "Dashboard updated with your functions!", delay: 2500 }
      ]
    }
  },
  workflow: {
    title: "Self-Learning Workflows",
    description: "Workflows that improve and adapt based on usage patterns",
    code: `# Define transition rules for common patterns
onefunction.add_transition_rule(
  from_tool="slack_message",
  to_tools=["email_followup", "calendar_schedule"],
  probability=0.8
)

# OneFunction learns from your patterns
result = onefunction({
  "task": "handle customer inquiry about billing",
  "data": {"message": "Need help with billing", "urgency": "high"}
})

# Automatically suggests and executes next steps:
# 1. Analyze sentiment and urgency
# 2. Route to billing support functions  
# 3. Suggest follow-up actions based on learned patterns
# 4. Create calendar event if escalation needed`,
    demo: {
      input: "handle customer inquiry about billing",
      steps: [
        { icon: MessageCircle, text: "Analyzing customer sentiment...", delay: 500 },
        { icon: IconAi, text: "Routing to billing support workflow", delay: 1000 },
        { icon: Workflow, text: "Executing learned pattern sequence", delay: 1500 },
        { icon: IconAutomation, text: "Suggesting follow-up actions", delay: 2000 },
        { icon: Check, text: "Workflow completed with suggestions!", delay: 2500 }
      ]
    }
  }
};

export function CodeExamples() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [demoStep, setDemoStep] = useState(0);

  const copyToClipboard = (code: string, key: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(key);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const runDemo = (key: string) => {
    setActiveDemo(key);
    setDemoStep(0);
    
    const example = codeExamples[key as keyof typeof codeExamples];
    example.demo.steps.forEach((step, index) => {
      setTimeout(() => {
        setDemoStep(index + 1);
        if (index === example.demo.steps.length - 1) {
          setTimeout(() => {
            setActiveDemo(null);
            setDemoStep(0);
          }, 2000);
        }
      }, step.delay);
    });
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-green-100 text-green-800 border-green-200">
            Interactive Examples
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            See Natural Language → Function Calls
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Watch how OneFunction automatically analyzes natural messages and routes them 
            to the right functions with zero configuration.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="simple" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
              <TabsTrigger value="simple">Natural Language</TabsTrigger>
              <TabsTrigger value="wrapper">LLM Wrapper</TabsTrigger>
              <TabsTrigger value="local">Local Functions</TabsTrigger>
              <TabsTrigger value="workflow">Smart Workflows</TabsTrigger>
            </TabsList>

            {Object.entries(codeExamples).map(([key, example]) => (
              <TabsContent key={key} value={key}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Code Example */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl text-slate-900">{example.title}</CardTitle>
                          <CardDescription className="mt-2">{example.description}</CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(example.code, key)}
                          className="flex items-center gap-2"
                        >
                          {copiedCode === key ? (
                            <>
                              <Check className="w-4 h-4" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-slate-900 rounded-lg p-6 overflow-x-auto">
                        <pre className="text-green-400 text-sm">
                          <code>{example.code}</code>
                        </pre>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Interactive Demo */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl text-slate-900">Live Demo</CardTitle>
                          <CardDescription className="mt-2">
                            See the function routing in action
                          </CardDescription>
                        </div>
                        <Button
                          onClick={() => runDemo(key)}
                          disabled={activeDemo === key}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        >
                          <Play className="w-4 h-4" />
                          {activeDemo === key ? 'Running...' : 'Run Demo'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Input */}
                        <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageCircle className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">Natural Input:</span>
                          </div>
                          <p className="text-blue-700 font-mono text-sm">"{example.demo.input}"</p>
                        </div>

                        {/* Steps */}
                        <div className="space-y-3">
                          {example.demo.steps.map((step, index) => {
                            const StepIcon = step.icon;
                            const isActive = activeDemo === key && demoStep > index;
                            const isCurrent = activeDemo === key && demoStep === index + 1;
                            
                            return (
                              <div
                                key={index}
                                className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                                  isActive 
                                    ? 'bg-green-50 border-green-200 border' 
                                    : isCurrent
                                    ? 'bg-blue-50 border-blue-200 border animate-pulse'
                                    : 'bg-gray-50 border-gray-200 border opacity-50'
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  isActive 
                                    ? 'bg-green-100' 
                                    : isCurrent
                                    ? 'bg-blue-100'
                                    : 'bg-gray-100'
                                }`}>
                                  <StepIcon className={`w-4 h-4 ${
                                    isActive 
                                      ? 'text-green-600' 
                                      : isCurrent
                                      ? 'text-blue-600'
                                      : 'text-gray-400'
                                  }`} />
                                </div>
                                <span className={`text-sm ${
                                  isActive 
                                    ? 'text-green-800 font-medium' 
                                    : isCurrent
                                    ? 'text-blue-800 font-medium'
                                    : 'text-gray-500'
                                }`}>
                                  {step.text}
                                </span>
                                {isActive && (
                                  <Check className="w-4 h-4 text-green-600 ml-auto" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Integration Examples */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Intelligent Function Routing
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              OneFunction automatically detects and routes to the right functions from your favorite tools and services.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { name: "OpenAI", type: "LLM" },
              { name: "Anthropic", type: "LLM" }, 
              { name: "Langchain", type: "Framework" },
              { name: "CrewAI", type: "Framework" },
              { name: "Slack", type: "Communication" },
              { name: "Discord", type: "Communication" },
              { name: "Gmail", type: "Email" },
              { name: "Notion", type: "Productivity" },
              { name: "AWS", type: "Cloud" },
              { name: "Azure", type: "Cloud" },
              { name: "GCP", type: "Cloud" },
              { name: "Vercel", type: "Deployment" }
            ].map((tool) => (
              <div key={tool.name} className="bg-white rounded-lg p-4 text-center border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                <div className="text-sm font-medium text-slate-700 mb-1">{tool.name}</div>
                <div className="text-xs text-slate-500">{tool.type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
      